// Authentication Service
// FIXED: Uses localStorage instead of sessionStorage for preAuthState
// sessionStorage doesn't persist across OAuth redirects in some browsers
import { supabase, isSupabaseConfigured } from './supabaseClient';

/**
 * Send OTP to email using Supabase Auth
 * @param {string} email - User's email address
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const sendOTP = async (email) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Authentication not configured' };
  }

  try {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: `${window.location.origin}/auth/callback`,
      },
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error sending OTP:', error);
    const msg = error.message || '';
    if (
      msg.toLowerCase().includes('failed to fetch') ||
      msg.toLowerCase().includes('networkerror') ||
      msg.toLowerCase().includes('fetch')
    ) {
      return {
        success: false,
        error: 'Unable to connect. Please check your internet connection and try again.',
      };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Verify OTP
 * @param {string} email - User's email address
 * @param {string} token - OTP token
 * @returns {Promise<{success: boolean, user?: object, error?: string}>}
 */
export const verifyOTP = async (email, token) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Authentication not configured' };
  }

  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });

    if (error) throw error;
    return { success: true, user: data.user, session: data.session };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sign in with Google using ID Token (Google Identity Services)
 * This replaces the OAuth redirect flow — users see "finncal.com" instead of Supabase URL
 * @param {string} credential - The ID token from Google GIS callback
 * @returns {Promise<{success: boolean, user?: object, session?: object, error?: string}>}
 */
export const signInWithGoogleIdToken = async (credential) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Authentication not configured' };
  }

  try {
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: credential,
    });

    if (error) throw error;
    return { success: true, user: data.user, session: data.session };
  } catch (error) {
    console.error('Error signing in with Google ID token:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Sign in with Google OAuth (legacy redirect flow — kept as fallback)
 * @param {object} options - Optional: { pendingSave: true, returnPath: '/current/path' }
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const signInWithGoogle = async (options = {}) => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Authentication not configured' };
  }

  try {
    const preAuthState = {
      returnPath: options.returnPath || window.location.pathname,
      pendingSave: options.pendingSave || false,
      timestamp: Date.now(),
    };

    localStorage.setItem('finncal_preAuthState', JSON.stringify(preAuthState));

    const returnPath = preAuthState.returnPath || '/';
    const redirectUrl = `${window.location.origin}/auth/callback?returnTo=${encodeURIComponent(returnPath)}`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    });

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error signing in with Google:', error);
    localStorage.removeItem('finncal_preAuthState');
    return { success: false, error: error.message };
  }
};

/**
 * Get pre-auth state (called after OAuth callback)
 * FIXED: Uses localStorage instead of sessionStorage
 * @returns {object|null} Pre-auth state or null if not found/expired
 */
export const getPreAuthState = () => {
  try {
    const stateStr = localStorage.getItem('finncal_preAuthState');
    if (!stateStr) {
      console.log('No pre-auth state found in localStorage');
      return null;
    }

    const state = JSON.parse(stateStr);
    console.log('Retrieved pre-auth state:', state);

    // Check if state is expired (5 minutes max)
    if (Date.now() - state.timestamp > 5 * 60 * 1000) {
      console.log('Pre-auth state expired, removing');
      localStorage.removeItem('finncal_preAuthState');
      return null;
    }

    return state;
  } catch (error) {
    console.error('Error getting pre-auth state:', error);
    return null;
  }
};

/**
 * Clear pre-auth state (called after it's been used)
 * FIXED: Uses localStorage
 */
export const clearPreAuthState = () => {
  console.log('Clearing pre-auth state');
  localStorage.removeItem('finncal_preAuthState');
};

/**
 * Sign out current user
 * FIXED: Also clears pre-auth state
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const signOut = async () => {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Authentication not configured' };
  }

  try {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear pre-auth state on sign out
    clearPreAuthState();

    return { success: true };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get current authenticated user
 * @returns {Promise<object|null>}
 */
export const getCurrentUser = async () => {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Get current session
 * @returns {Promise<object|null>}
 */
export const getSession = async () => {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return session;
  } catch (error) {
    console.error('Error getting session:', error);
    return null;
  }
};

/**
 * Subscribe to authentication state changes
 * @param {function} callback - Function to call on auth state change
 * @returns {object} Subscription object with unsubscribe method
 */
export const onAuthStateChange = (callback) => {
  if (!isSupabaseConfigured()) {
    return { unsubscribe: () => {} };
  }

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session);
  });

  return subscription;
};
